import { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Settings, Users, Shield, Eye, EyeOff, Plus, Trash2, Copy, Check, Smartphone, Upload, X } from 'lucide-react';
import { cn } from '../lib/utils';

export default function AdminPage() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [newUserDlg, setNewUserDlg] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: '', password: '', name: '', role: 'user' });
  const [savingUser, setSavingUser] = useState(false);

  // Microsoft Graph
  const [graphForm, setGraphForm] = useState({ tenant_azure_id: '', client_id: '', client_secret: '', redirect_uri: '' });
  const [graphConfigured, setGraphConfigured] = useState(false);
  const [savingGraph, setSavingGraph] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAResult, setTwoFAResult] = useState(null);
  const [setting2FA, setSetting2FA] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [savingPw, setSavingPw] = useState(false);
  // Logo
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const load = async () => {
    const [s, u, g] = await Promise.all([api.get('/admin/settings'), api.get('/admin/users'), api.get('/admin/graph-config')]);
    setSettings(s.data);
    setLogoPreview(s.data.logo || null);
    setSettingsForm({
      smtp_host: s.data.smtp_host || '',
      smtp_port: s.data.smtp_port || 587,
      smtp_user: s.data.smtp_user || '',
      smtp_password: '',
      smtp_from: s.data.smtp_from || '',
      auto_reminders_enabled: s.data.auto_reminders_enabled !== false,
      mwst_rate: s.data.mwst_rate || 8.1,
      overdue_days_red: s.data.overdue_days_red || 3,
      company_name: s.data.company_name || '',
      company_address: s.data.company_address || '',
      mwst_number: s.data.mwst_number || '',
      phone: s.data.phone || '',
      email: s.data.email || '',
      website: s.data.website || '',
      payment_terms: s.data.payment_terms || 'Zahlbar innert 30 Tagen netto.',
      bank_info: s.data.bank_info || '',
      iban: s.data.iban || '',
    });
    setUsers(u.data);
    setGraphForm({ tenant_azure_id: g.data.tenant_azure_id || '', client_id: g.data.client_id || '', client_secret: '', redirect_uri: g.data.redirect_uri || '' });
    setGraphConfigured(g.data.configured);
  };

  useEffect(() => { load().catch(() => toast.error('Fehler')).finally(() => setLoading(false)); }, []);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.logo) {
        setLogoPreview(res.data.logo);
        toast.success('Logo hochgeladen');
      } else {
        toast.error('Logo konnte nicht gespeichert werden');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      if (logoInputRef.current) logoInputRef.current.value = '';
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Logo entfernen?')) return;
    await api.delete('/admin/logo');
    setLogoPreview(null);
    toast.success('Logo entfernt');
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...settingsForm, smtp_port: parseInt(settingsForm.smtp_port) || 587, mwst_rate: parseFloat(settingsForm.mwst_rate) || 8.1, overdue_days_red: parseInt(settingsForm.overdue_days_red) || 3 };
      if (!payload.smtp_password) delete payload.smtp_password;
      await api.put('/admin/settings', payload);
      toast.success('Einstellungen gespeichert');
      await load();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const handleNewUser = async () => {
    if (!newUserForm.email || !newUserForm.password || !newUserForm.name) { toast.error('Alle Felder pflicht'); return; }
    setSavingUser(true);
    try {
      await api.post('/admin/users', newUserForm);
      toast.success('Benutzer erstellt');
      setNewUserDlg(false);
      setNewUserForm({ email: '', password: '', name: '', role: 'user' });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSavingUser(false); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    await api.delete(`/admin/users/${id}`);
    toast.success('Gelöscht');
    await load();
  };

  const handleSaveGraph = async () => {
    setSavingGraph(true);
    try {
      await api.put('/admin/graph-config', graphForm);
      toast.success('Graph-Konfiguration gespeichert');
      setGraphConfigured(!!graphForm.client_id);
    } catch { toast.error('Fehler'); } finally { setSavingGraph(false); }
  };

  const handleSetup2FA = async () => {
    setSetting2FA(true);
    try {
      const res = await api.post('/auth/2fa/setup/totp');
      setTwoFASetup(res.data);
    } catch { toast.error('Fehler'); } finally { setSetting2FA(false); }
  };

  const handleConfirm2FA = async () => {
    if (!twoFACode) { toast.error('Code eingeben'); return; }
    try {
      const res = await api.post('/auth/2fa/confirm/totp', { secret: twoFASetup.secret, code: twoFACode });
      setTwoFAResult(res.data);
      setTwoFASetup(null);
      toast.success('2FA aktiviert!');
      await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ungültiger Code'); }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('2FA wirklich deaktivieren?')) return;
    await api.delete('/auth/2fa/disable');
    toast.success('2FA deaktiviert');
    await refreshUser();
  };

  const handleChangePw = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('Beide Felder ausfüllen'); return; }
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', pwForm);
      toast.success('Passwort geändert');
      setPwForm({ current_password: '', new_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSavingPw(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          <h1 className="font-heading font-black text-3xl">Einstellungen</h1>
        </div>

        <Tabs defaultValue="general">
          <TabsList className="rounded-sm mb-6">
            <TabsTrigger value="general" className="rounded-sm">Allgemein</TabsTrigger>
            <TabsTrigger value="smtp" className="rounded-sm">SMTP / E-Mail</TabsTrigger>
            <TabsTrigger value="graph" className="rounded-sm">Microsoft Graph</TabsTrigger>
            <TabsTrigger value="users" className="rounded-sm" data-testid="users-tab">Benutzer</TabsTrigger>
            <TabsTrigger value="security" className="rounded-sm">Sicherheit</TabsTrigger>
          </TabsList>

          {/* General */}
          <TabsContent value="general" className="space-y-4">
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Unternehmenslogo</h3>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 border-2 border-dashed border-border rounded-sm flex items-center justify-center overflow-hidden bg-muted">
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-1" />
                    : <Shield className="w-6 h-6 text-muted-foreground/50" />}
                </div>
                <div className="space-y-2">
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e.target.files[0])} />
                  <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} className="rounded-sm gap-2">
                    <Upload className="w-3.5 h-3.5" />{logoUploading ? 'Hochladen...' : 'Logo hochladen'}
                  </Button>
                  {logoPreview && (
                    <Button variant="ghost" size="sm" onClick={handleLogoDelete} className="rounded-sm gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
                      <X className="w-3.5 h-3.5" />Logo entfernen
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground">PNG, JPG, GIF, WEBP – max. 500 KB. Erscheint auf Login, Sidebar und PDFs.</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Unternehmen</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Unternehmensname</Label><Input data-testid="company-name-setting" value={settingsForm.company_name || ''} onChange={e => setSettingsForm(f => ({ ...f, company_name: e.target.value }))} className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>Adresse</Label><Input value={settingsForm.company_address || ''} onChange={e => setSettingsForm(f => ({ ...f, company_address: e.target.value }))} className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>MwSt-Nummer (z.B. CHE-212.047.452)</Label><Input value={settingsForm.mwst_number || ''} onChange={e => setSettingsForm(f => ({ ...f, mwst_number: e.target.value }))} placeholder="CHE-000.000.000" className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>Telefon</Label><Input value={settingsForm.phone || ''} onChange={e => setSettingsForm(f => ({ ...f, phone: e.target.value }))} placeholder="+41 79 000 00 00" className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>E-Mail (Kontakt)</Label><Input type="email" value={settingsForm.email || ''} onChange={e => setSettingsForm(f => ({ ...f, email: e.target.value }))} placeholder="info@unternehmen.ch" className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>Website</Label><Input value={settingsForm.website || ''} onChange={e => setSettingsForm(f => ({ ...f, website: e.target.value }))} placeholder="www.unternehmen.ch" className="rounded-sm" /></div>
              </div>
              <p className="text-xs text-muted-foreground">Diese Angaben erscheinen im Kopfbereich der generierten PDFs.</p>
            </div>
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Rechnungseinstellungen</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>MwSt-Satz (%)</Label>
                  <Input type="number" step="0.1" data-testid="mwst-rate-input" value={settingsForm.mwst_rate || ''} onChange={e => setSettingsForm(f => ({ ...f, mwst_rate: e.target.value }))} className="rounded-sm" />
                  <p className="text-xs text-muted-foreground">Schweiz: 8.1% (Normal), 3.8% (Beherbergung), 2.6% (Reduziert)</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Rot-Markierung nach X Tagen Verzug</Label>
                  <Input type="number" data-testid="overdue-days-input" value={settingsForm.overdue_days_red || ''} onChange={e => setSettingsForm(f => ({ ...f, overdue_days_red: e.target.value }))} className="rounded-sm" />
                  <p className="text-xs text-muted-foreground">Auftragskarteien werden nach dieser Anzahl Tage rot markiert</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Zahlungsbedingungen (erscheint auf PDF)</Label>
                <textarea
                  value={settingsForm.payment_terms || ''}
                  onChange={e => setSettingsForm(f => ({ ...f, payment_terms: e.target.value }))}
                  className="w-full min-h-[80px] px-3 py-2 text-sm border border-input bg-background rounded-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Zahlbar innert 30 Tagen netto. Bei Verzug werden Verzugszinsen von 5% berechnet."
                  data-testid="payment-terms-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>IBAN (für QR-Rechnung)</Label>
                  <Input value={settingsForm.iban || ''} onChange={e => setSettingsForm(f => ({ ...f, iban: e.target.value }))} placeholder="CH00 0000 0000 0000 0000 0" className="rounded-sm font-mono" />
                  <p className="text-xs text-muted-foreground">QR-IBAN erzeugt eine QR-Referenz, normale IBAN eine RF-Referenz. Betrag und Auftragsnummer werden automatisch übernommen.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Bankname (erscheint im Fussbereich)</Label>
                  <Input value={settingsForm.bank_info || ''} onChange={e => setSettingsForm(f => ({ ...f, bank_info: e.target.value }))} placeholder="UBS Schweiz AG" className="rounded-sm" data-testid="bank-info-input" />
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveSettings} disabled={saving} className="rounded-sm" data-testid="save-settings-btn">
                {saving ? 'Speichern...' : 'Einstellungen speichern'}
              </Button>
            </div>
          </TabsContent>

          {/* SMTP */}
          <TabsContent value="smtp" className="space-y-4">
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">SMTP-Konfiguration</h3>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm p-4">
                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">SMTP-Zugangsdaten</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Tragen Sie hier Ihre SMTP-Zugangsdaten ein, um PDF-Rechnungen direkt per E-Mail zu versenden.
                  Die Zugangsdaten werden verschlüsselt gespeichert.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>SMTP Host</Label><Input data-testid="smtp-host-input" value={settingsForm.smtp_host || ''} onChange={e => setSettingsForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="smtp.gmail.com" className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>Port</Label><Input type="number" value={settingsForm.smtp_port || 587} onChange={e => setSettingsForm(f => ({ ...f, smtp_port: e.target.value }))} className="rounded-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Benutzername</Label><Input value={settingsForm.smtp_user || ''} onChange={e => setSettingsForm(f => ({ ...f, smtp_user: e.target.value }))} placeholder="user@gmail.com" className="rounded-sm" /></div>
                <div className="space-y-1.5">
                  <Label>Passwort</Label>
                  <div className="relative">
                    <Input type={showSmtpPass ? 'text' : 'password'} value={settingsForm.smtp_password || ''} onChange={e => setSettingsForm(f => ({ ...f, smtp_password: e.target.value }))} placeholder="Leer = unverändert" className="rounded-sm pr-10" data-testid="smtp-password-input" />
                    <button type="button" onClick={() => setShowSmtpPass(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                      {showSmtpPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Absender E-Mail</Label><Input type="email" value={settingsForm.smtp_from || ''} onChange={e => setSettingsForm(f => ({ ...f, smtp_from: e.target.value }))} placeholder="noreply@unternehmen.ch" className="rounded-sm" /></div>
              <div className="flex items-center justify-between border border-border/60 rounded-sm p-4">
                <div>
                  <Label className="text-sm font-medium">Automatische Zahlungserinnerung</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sendet die erste Mahnung automatisch 5 Tage nach Ablauf der Zahlungsfrist.
                  </p>
                </div>
                <Switch
                  checked={settingsForm.auto_reminders_enabled !== false}
                  onCheckedChange={checked => setSettingsForm(f => ({ ...f, auto_reminders_enabled: checked }))}
                  data-testid="auto-reminders-switch"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveSettings} disabled={saving} className="rounded-sm" data-testid="save-smtp-btn">
                {saving ? 'Speichern...' : 'SMTP speichern'}
              </Button>
            </div>
          </TabsContent>

          {/* Microsoft Graph */}
          <TabsContent value="graph" className="space-y-4">
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">Microsoft Graph / Outlook Kalender</h3>
                {graphConfigured && <span className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded-sm">Konfiguriert</span>}
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-sm p-4 space-y-1">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Azure App Registration benötigt</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">1. Azure Portal → App Registrierungen → Neue Registrierung</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">2. API-Berechtigungen: Calendars.ReadWrite, Mail.Send</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">3. Client Secret erstellen und hier eintragen</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Azure Tenant ID</Label>
                  <Input value={graphForm.tenant_azure_id} onChange={e => setGraphForm(f => ({ ...f, tenant_azure_id: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="rounded-sm font-mono text-sm" data-testid="graph-tenant-id" />
                </div>
                <div className="space-y-1.5">
                  <Label>Client ID (App ID)</Label>
                  <Input value={graphForm.client_id} onChange={e => setGraphForm(f => ({ ...f, client_id: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="rounded-sm font-mono text-sm" data-testid="graph-client-id" />
                </div>
                <div className="space-y-1.5">
                  <Label>Client Secret</Label>
                  <Input type="password" value={graphForm.client_secret} onChange={e => setGraphForm(f => ({ ...f, client_secret: e.target.value }))} placeholder="Leer = unverändert" className="rounded-sm" data-testid="graph-client-secret" />
                </div>
                <div className="space-y-1.5">
                  <Label>Redirect URI</Label>
                  <Input value={graphForm.redirect_uri} onChange={e => setGraphForm(f => ({ ...f, redirect_uri: e.target.value }))} placeholder="https://yourhero.ch/api/auth/graph/callback" className="rounded-sm" />
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveGraph} disabled={savingGraph} className="rounded-sm" data-testid="save-graph-btn">
                {savingGraph ? 'Speichern...' : 'Graph-Konfiguration speichern'}
              </Button>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{users.length} Benutzer</p>
              <Button onClick={() => setNewUserDlg(true)} size="sm" className="rounded-sm gap-2" data-testid="new-user-btn">
                <Plus className="w-3.5 h-3.5" />Benutzer
              </Button>
            </div>
            <div className="space-y-1.5">
              {users.map(u => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between p-4 bg-card border border-border/60 rounded-sm">
                  <div>
                    <p className="font-medium text-sm text-foreground">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn("text-xs px-2 py-0.5 rounded-sm font-medium", 
                      u.role === 'admin' ? 'bg-primary/10 text-primary' : 
                      u.role === 'viewer' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' :
                      u.role === 'buchhalter' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                      'bg-muted text-muted-foreground')}>
                      {u.role === 'admin' ? 'Administrator' : 
                       u.role === 'viewer' ? 'Nur-Lesen' : 
                       u.role === 'buchhalter' ? 'Buchhalter' : 'Benutzer'}
                    </span>
                    {u.two_fa_enabled && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded-sm">2FA</span>
                    )}
                    {u.id !== user?.id && (
                      <button onClick={() => handleDeleteUser(u.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1.5" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security" className="space-y-4">
            {/* 2FA */}
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                <h3 className="font-heading font-semibold text-base">Zwei-Faktor-Authentifizierung</h3>
                {user?.two_fa_enabled && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded-sm ml-auto">Aktiv</span>
                )}
              </div>
              {!user?.two_fa_enabled ? (
                <>
                  <p className="text-sm text-muted-foreground">Schützen Sie Ihr Konto mit einem TOTP-Authenticator (Google Authenticator, Authy, etc.).</p>
                  {!twoFASetup ? (
                    <Button onClick={handleSetup2FA} disabled={setting2FA} className="rounded-sm" data-testid="setup-2fa-btn">
                      {setting2FA ? 'Generieren...' : '2FA einrichten'}
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex flex-col items-center gap-4 p-4 bg-muted rounded-sm">
                        <img src={twoFASetup.qr_code} alt="QR Code" className="w-48 h-48" />
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Oder manuell eingeben:</p>
                          <p className="font-mono text-sm font-bold text-foreground bg-background px-3 py-1.5 rounded-sm border">{twoFASetup.secret}</p>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Code aus Authenticator-App</Label>
                        <Input data-testid="2fa-setup-code" value={twoFACode} onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" className="rounded-sm font-mono text-center text-xl tracking-widest" maxLength={6} />
                      </div>
                      <Button onClick={handleConfirm2FA} className="rounded-sm w-full" data-testid="confirm-2fa-btn">2FA bestätigen & aktivieren</Button>
                    </div>
                  )}
                  {twoFAResult && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm p-4">
                      <p className="font-medium text-sm text-amber-700 dark:text-amber-300 mb-2">Backup-Codes – Sicher aufbewahren!</p>
                      <div className="grid grid-cols-4 gap-2">
                        {twoFAResult.backup_codes.map((code, i) => (
                          <span key={i} className="font-mono text-xs bg-background border border-border rounded-sm px-2 py-1 text-center">{code}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">2FA ist aktiv. Methode: TOTP (Authenticator App)</p>
                  <Button variant="outline" onClick={handleDisable2FA} className="rounded-sm border-destructive/50 text-destructive hover:bg-destructive/10" data-testid="disable-2fa-btn">
                    2FA deaktivieren
                  </Button>
                </div>
              )}
            </div>

            {/* Password Change */}
            <div className="bg-card border border-border/60 rounded-sm p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                <h3 className="font-heading font-semibold text-base">Passwort ändern</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Aktuelles Passwort</Label><Input type="password" data-testid="current-password-input" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>Neues Passwort</Label><Input type="password" data-testid="new-password-input" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} className="rounded-sm" /></div>
              </div>
              <Button onClick={handleChangePw} disabled={savingPw} className="rounded-sm" data-testid="change-password-btn">
                {savingPw ? 'Speichern...' : 'Passwort ändern'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={newUserDlg} onOpenChange={setNewUserDlg}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Neuer Benutzer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="new-user-name" value={newUserForm.name} onChange={e => setNewUserForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" data-testid="new-user-email" value={newUserForm.email} onChange={e => setNewUserForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Passwort</Label><Input type="password" data-testid="new-user-password" value={newUserForm.password} onChange={e => setNewUserForm(f => ({ ...f, password: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5">
              <Label>Rolle</Label>
              <select value={newUserForm.role} onChange={e => setNewUserForm(f => ({ ...f, role: e.target.value }))} className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" data-testid="new-user-role">
                <option value="admin">Administrator - Vollzugriff</option>
                <option value="user">Benutzer - Erstellen & Bearbeiten</option>
                <option value="viewer">Nur-Lesen - Ansicht & PDF</option>
                <option value="buchhalter">Buchhalter - Nur Phase 4-5</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {newUserForm.role === 'admin' && 'Kann alle Einstellungen und Benutzer verwalten'}
                {newUserForm.role === 'user' && 'Kann Aufträge und Offerten erstellen, aber keine Einstellungen ändern'}
                {newUserForm.role === 'viewer' && 'Kann nur anzeigen und PDFs herunterladen, keine Änderungen'}
                {newUserForm.role === 'buchhalter' && 'Kann nur Rechnungsphase (4) und Abgeschlossen (5) bearbeiten'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUserDlg(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleNewUser} disabled={savingUser} className="rounded-sm" data-testid="save-new-user-btn">{savingUser ? 'Erstellen...' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
